import { FlowRunDetail } from "@/models/flows";
import { useQuery } from "react-query";
import axios from "axios";
import { BASE_URL } from "@/dashboardQueries";
import { ViewResponseDialog } from "@/components/dashboard/forms/ViewResponseDialog";
import { useState } from "react";
import { InputData } from "../InputData";

interface Props {
  flowRun: FlowRunDetail;
  expandTriggerInput?: boolean;
}

const fetchFormById = async (formId: string) => {
  const response = await axios.get(`${BASE_URL}/dashboard/forms/${formId}`);
  return response.data;
};

const fetchFormResponseById = async (formId: string, responseId: string) => {
  const response = await axios.get(
    `${BASE_URL}/dashboard/forms/${formId}/responses/${responseId}`
  );
  return response.data;
};

export const FormSubmitted = ({ flowRun, expandTriggerInput }: Props) => {
  const formId = flowRun.trigger.config?.formId;
  const responseId =
    flowRun.stepRuns[0].inputData.triggers[flowRun.trigger.uuid].responseId;
  const [viewResponseOpen, setViewResponseOpen] = useState(false);

  const {
    data: form,
    isLoading: isFormLoading,
    isError: isFormError,
  } = useQuery(["form", formId], () => fetchFormById(formId), {
    enabled: !!formId, // Only fetch if formId exists
  });

  const fieldMap = form?.fields.reduce((acc, field) => {
    acc[field.uuid] = field.name;
    return acc;
  }, {});

  const {
    data: formResponse,
    isLoading: isResponseLoading,
    isError: isResponseError,
  } = useQuery(
    ["formResponse", responseId],
    () => fetchFormResponseById(formId, responseId),
    {
      enabled: !!responseId, // Only fetch if responseId exists
    }
  );

  return (
    <div className="ml-9 pb-4">
      {isFormLoading && <p>Loading form...</p>}
      {isFormError && <p>Error loading form.</p>}
      {form ? (
        <div className="">
          <div className="-ml-9">
            <InputData
              inputData={Object.fromEntries(
                Object.entries(flowRun.triggerInput).map(([key, value]) => [
                  fieldMap[key] || key,
                  value,
                ])
              )}
              dataUncollapsed={expandTriggerInput}
            />
          </div>
          {flowRun.displayName && (
            <div className="dark:text-white mb-1 font-semibold">
              {flowRun.displayName}
            </div>
          )}
          {formResponse && (
            <ViewResponseDialog
              open={viewResponseOpen}
              setOpen={setViewResponseOpen}
              form={form}
              formResponse={formResponse}
            />
          )}
          <p className="dark:text-white">{form.name}</p>
          <button
            id="form-responses"
            onClick={() => setViewResponseOpen(true)}
            className="flex mt-3 items-center rounded-md bg-gray-400/10 px-2 py-1 text-xs font-medium text-gray-400 ring-1 ring-inset ring-gray-400/20"
          >
            <div className="">View this response</div>
          </button>
        </div>
      ) : (
        <p>No form associated with this trigger.</p>
      )}
      {isResponseLoading && <p>Loading form response...</p>}
      {isResponseError && <p>Error loading form response.</p>}
    </div>
  );
};
